import type { CompanyProfile, TenantContractStatus } from '@felix/domain';
import {
  type ClockSchedule,
  type MaterialClass,
  type RaMatrixDefinition,
  type TestAccounts,
  testAccountsSchema,
} from '@felix/domain';
// Tedarikçi kartında profil ve banka (ADR 0114)
import type {
  ApprovalSettings,
  AuditTrailData,
  AuditTrailFilter,
  AwardProposalInput,
  BankReviewAction,
  CatalogImportReport,
  CertificateInput,
  CertificateType,
  CharterPartyAnalysisData,
  CharterPartyDecisionInput,
  CharterPartyEventView,
  CharterPartyItem,
  CharterPartyRegisterData,
  CmReport,
  CompanyPort,
  CompanyPortInput,
  CrewCardResult,
  CrewCardUpdate,
  CrewCertificates,
  CrewMember,
  CrewMovementList,
  CrewProfileView,
  CrewRestReport,
  DecisionInput,
  DefectDetail,
  DefectList,
  DefectListResult,
  DefectView,
  DistanceImportReport,
  EquipmentDetail,
  EquipmentTree,
  GoodsReceiptDetail,
  GoodsReceiptList,
  HistoryEntry,
  HistoryFilter,
  IntervalHistoryEntry,
  JobChangeEntry,
  JobList,
  JobView,
  LibraryTree,
  MasterDeputySettings,
  MaterialCategory,
  MaterialDetail,
  MaterialInput,
  MaterialList,
  MaterialQuery,
  Measurement,
  MeasurementPoint,
  MovementEvent,
  MovementQuery,
  MovementResult,
  NewCrewCardInput,
  OfficeDashboardSummary,
  Partner,
  PartnerQuery,
  PayrollData,
  PendingBankChange,
  PendingIssuanceNotice,
  PendingItem,
  PendingLedger,
  PendingRequisition,
  PlanView,
  PmsAlertsView,
  PmsAttachment,
  PmsReportFilter,
  PmsReportView,
  PmsSettings,
  PointList,
  PortCall,
  PortCallList,
  PortDistance,
  PortDistanceHistoryEvent,
  PortDistanceInput,
  PortDistancePatch,
  PostponementView,
  PurchaseOrderCandidate,
  PurchaseOrderDeliveryInput,
  PurchaseOrderDetail,
  PurchaseOrderList,
  QueuedSetupChange,
  QuotationInput,
  Reading,
  ReportData,
  ReportKind,
  ReportQuery,
  RequisitionDecision,
  RequisitionDetail,
  RequisitionList,
  RequisitionRevisionInput,
  RestMonth,
  RevealedBankNumber,
  RfqAwards,
  RfqDetail,
  RfqInput,
  RfqList,
  RfqPublishInput,
  RiskMatrixSettings,
  RotableList,
  SetupChangeListView,
  SetupChangeQuery,
  ShipClockData,
  SignOnRequest,
  StockMovement,
  Supplier,
  SupplierInput,
  SupplierList,
  SupplierProfileView,
  SupplierReviewsView,
  VesselParticulars,
  VesselParticularsInput,
  VesselStock,
  Voyage,
  VoyageRequest as VoyageOfficeRequest,
  VoyagePerformanceView,
  VoyageReport,
  VoyageRequestInput,
  VoyageRoute,
  WorkOrderList,
  WorkOrderView,
  WorkPeriod,
} from '@felix/ui';

export type {
  CertificateInput,
  CertificateRecord,
  CertificateStatus,
  CertificateType,
  CrewCertificates,
  CrewMember,
  CrewRestDay,
  CrewRestReport,
  RestFinding,
  WorkPeriod,
} from '@felix/ui';

import { reportAccessDenied } from '@felix/ui';
import { clearUploadResumes } from './tus-upload.js';

/** API hatası: sunucu `{ error: CODE }` döner; ağ hatasında `NETWORK`. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  /** Sunucunun gösterilebilir ayrıntı kodu (ör. alan hataları); yoksa `undefined`. */
  readonly detail: string | undefined;
  /** Yanıt gövdesi (ör. ağaç işleminde gemi başına hatalar; ADR 0038 §8). */
  readonly payload: unknown;
  constructor(code: string, status: number, detail?: string, payload?: unknown) {
    super(code);
    this.code = code;
    this.status = status;
    this.detail = detail;
    this.payload = payload;
  }
}

export type SessionStage =
  | 'password_change_required'
  | 'totp_enrollment_required'
  | 'totp_required'
  | 'active';

/**
 * API istemcisi: aynı alan adında `/api` (mimari belge §10.2). Oturum HttpOnly cookie'de; tarayıcı
 * kendisi gönderir, kodda token yoktur (§10.3).
 */
export async function api<T>(
  path: string,
  init: { method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'; body?: unknown } = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method: init.method ?? 'GET',
      credentials: 'same-origin',
      headers: init.body === undefined ? {} : { 'content-type': 'application/json' },
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  const data = (await response.json().catch(() => ({}))) as { error?: string; detail?: unknown };
  if (!response.ok) {
    // Okuma yetkisiz: sayfanın hata durumu "yeniden dene" yerine yetki yok olur (ADR 0067 §6)
    if (response.status === 403 && data.error === 'FORBIDDEN' && (init.method ?? 'GET') === 'GET') {
      reportAccessDenied();
    }
    throw new ApiError(
      data.error ?? 'INTERNAL',
      response.status,
      typeof data.detail === 'string' ? data.detail : undefined,
      data,
    );
  }
  return data as T;
}

/**
 * Katalog dosyasının yüklenmesi (ADR 0026 §4): ham `.xlsx` gövdesi, en çok 10 MB. JSON olmayan
 * tek istek; oturum cookie'si tarayıcıdan.
 */
export async function uploadCatalog(file: Blob): Promise<CatalogImportReport> {
  let response: Response;
  try {
    response = await fetch('/api/office/materials/import', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      body: file,
    });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  const data = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) throw new ApiError(data.error ?? 'INTERNAL', response.status);
  return data as CatalogImportReport;
}

/**
 * NR496 raporunun Excel ya da CSV dosyası (plan M7 Task 29): sunucuda üretilir; ad yanıt
 * başlığından (`felix-manager-<sürüm>-<rapor>-<YYYYMMDD>.<uzantı>`). Oturum cookie'si tarayıcıdan.
 */
export async function pmsReportFile(
  vesselId: string,
  reportId: string,
  options: { format: string; lang: string; query?: PmsReportFilter | undefined },
): Promise<{ fileName: string; bytes: Uint8Array; type: string }> {
  let response: Response;
  try {
    response = await fetch(
      `/api/office/vessels/${encodeURIComponent(vesselId)}/pms/reports/${encodeURIComponent(reportId)}/export${queryString({ ...options.query, format: options.format, lang: options.lang })}`,
      { credentials: 'same-origin' },
    );
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(data.error ?? 'INTERNAL', response.status);
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `${reportId}.${options.format}`;
  return {
    fileName,
    bytes: new Uint8Array(await response.arrayBuffer()),
    type: response.headers.get('content-type') ?? 'application/octet-stream',
  };
}

/** Sorgu dizesi: boş ve `undefined` değerler atlanır. */
function queryString(values: Record<string, string | number | boolean | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === '' || value === false) continue;
    params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export interface Me {
  stage: SessionStage;
  user: { id: string; email?: string; fullName?: string };
  /** Şirketin onay ayarı (ADR 0056 §4); eski sunucu göndermez. */
  approvalPolicy?: { reauthAlways: boolean };
  /** Görebildiği kısıtlı modüller (ADR 0061 §6); eski sunucu göndermez (hepsi gizli). */
  viewableModules?: string[];
  /** Settings ▸ Users bağlantısı: şirket düzeyinde `users.view` (ortak 2026-10-30). */
  canViewUsers?: boolean;
  /**
   * İki adımlı doğrulama isteğe bağlı (sahip 2026-11-01): `available=false` tanıtım şirketinde ve
   * paylaşılan test hesabında. Eski sunucu göndermez.
   */
  twoStep?: TwoStepState;
}

export interface TwoStepState {
  enabled: boolean;
  available: boolean;
}

export interface Vessel {
  id: string;
  name: string;
  imo: string | null;
  /** IANA saat dilimi; gemide "işin yapıldığı gün" buna göre (mimari belge §6.11). */
  timeZone: string;
  /** Gemi saati çizelgesi (onaylı saat değişiklikleri; ADR 0065 §4); eski sunucu göndermez. */
  clock?: ClockSchedule;
  /** Gemi sunucusu etkinleştirildi mi (mimari belge §8). */
  activated: boolean;
  /** Kurulum durumu (ADR 0142 §1); eski sunucu göndermez (kurulum sayılır). */
  setupState?: 'setup' | 'live';
  liveAt?: string | null;
  /** Uygulanan doküman şablonu ve sürümü (ADR 0038 §5); şablonsuz eski gemide `null`. */
  docTemplate?: { id: string; name: string | null; version: number } | null;
}

/**
 * Fleet sayfasının gemi satırı (`GET /api/office/fleet`; ADR 0151 §1): gemi listesinin aynısı,
 * üstüne düğmelerin yetki bayrakları. Bayrak yoksa (eski sunucu) düğme eskisi gibi görünür; sunucu
 * yine denetler.
 */
export interface FleetVessel extends Vessel {
  /** Kurulum kodu (`devices.create`, gemiye göre). */
  canSetupCode?: boolean;
  /** Gemi sunucusunu değiştirme: aynı uç, aynı yetki. */
  canReplaceServer?: boolean;
  /** "Go live" (`settings.edit`, gemiye göre; ADR 0142 §2). */
  canGoLive?: boolean;
}

export interface FleetList {
  /** Gemi ekleme (`settings.create`, şirket). */
  canCreateVessel?: boolean;
  /**
   * Gemi özelliklerini düzenleme (`settings.edit`, filo; Go live ile aynı soru; ADR 0151 Ek).
   * Eski sunucu göndermez: çekmece eskisi gibi düzenlenir, sunucu yine denetler.
   */
  canEditParticulars?: boolean;
  vessels: FleetVessel[];
}

export interface NewVessel {
  name: string;
  imo: string;
  timeZone: string;
  /** Doküman şablonu (ADR 0037 §4): şirkete açık şablonlardan. */
  docTemplateId: string;
}

/** Gemi ekleme formunun şablon seçeneği (`GET /api/office/doc-templates`). */
export interface DocTemplateOption {
  id: string;
  name: string;
  description: string | null;
  version: number;
  isDefault: boolean;
}

/** Gemi sunucusu kurulum kodu: tek kullanımlık, 24 saat geçerli (mimari belge §5.2). */
export interface SetupCode {
  code: string;
  expiresAt: string;
}

/** Şirket markası (ADR 0019 §4): sol üst marka bloğu ve gemi rozetinin sloganı. */
export interface Branding {
  id: string;
  name: string | null;
  tagline: string | null;
  /** `data:image/...;base64,...` (en çok 64 KB) */
  logo: string | null;
  vesselSlogan: string | null;
  rowVersion: number;
}

export interface BrandingInput {
  rowVersion?: number;
  name: string | null;
  tagline: string | null;
  logo: string | null;
  vesselSlogan: string | null;
}

/**
 * Bağlı test ortamı hesapları (ADR 0092; `GET /api/demo/test-accounts`, kimliksiz). Sunucuda
 * ayar kapalıysa (404), hata ya da beklenmeyen cevapta `null`: giriş ekranında panel yok.
 */
export async function fetchTestAccounts(): Promise<TestAccounts | null> {
  try {
    const response = await fetch('/api/demo/test-accounts', {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (response.status !== 200) return null;
    const parsed = testAccountsSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export const officeApi = {
  me: () => api<Me>('/office/auth/me'),
  login: (email: string, password: string) =>
    api<{ stage: SessionStage }>('/office/auth/login', {
      method: 'POST',
      body: { email, password },
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api<{ stage: SessionStage }>('/office/auth/password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    }),
  // Açık oturumda parola onayıyla (Ayarlar ▸ Hesap); eski giriş aşaması parolasız (sahip 2026-11-01)
  enrollTotp: (password?: string) =>
    api<{ secret: string; otpauthUri: string }>('/office/auth/totp/enroll', {
      method: 'POST',
      body: password === undefined ? {} : { password },
    }),
  confirmTotp: (code: string) =>
    api<{ stage: SessionStage }>('/office/auth/totp/confirm', { method: 'POST', body: { code } }),
  verifyTotp: (code: string) =>
    api<{ stage: SessionStage }>('/office/auth/totp/verify', { method: 'POST', body: { code } }),
  // Kişinin diğer oturumları kapanır, bu oturum açık kalır
  disableTotp: (password: string, code: string) =>
    api<{ twoStep: TwoStepState }>('/office/auth/totp/disable', {
      method: 'POST',
      body: { password, code },
    }),
  // Çıkışta yarım yüklemelerin devam kayıtları silinir (inceleme M8a3-R22)
  logout: () => {
    clearUploadResumes();
    return api<{ stage: null }>('/office/auth/logout', { method: 'POST', body: {} });
  },
  vessels: () => api<Vessel[]>('/office/vessels'),
  /** Fleet sayfası: gemiler ve düğmelerin yetki bayrakları (ADR 0151 §1). */
  fleet: () => api<FleetList>('/office/fleet'),
  // "Kullanıma al" (ADR 0142 §2): kurulumdaki etkin gemi canlıya geçer
  goLive: (vesselId: string) =>
    api<{ vesselId: string; state: 'setup' | 'live' }>(`/office/vessels/${vesselId}/go-live`, {
      method: 'POST',
      body: {},
    }),
  docTemplates: () =>
    api<{ defaultTemplateId: string | null; templates: DocTemplateOption[] }>(
      '/office/doc-templates',
    ),
  createVessel: (vessel: NewVessel) =>
    api<{ id: string; name: string; imo: string }>('/office/vessels', {
      method: 'POST',
      body: vessel,
    }),
  /** Kurulum kodu: kritik işlem, işlemi yapanın parolasıyla (ADR 0075 §5, ADR 0151 §2). */
  createSetupCode: (vesselId: string, password: string) =>
    api<SetupCode>(`/office/vessels/${vesselId}/setup-codes`, {
      method: 'POST',
      body: { password },
    }),
  // `canSignOn`: New Crew Card ▸ "Sign on now" (ADR 0131 Ek 4 §2)
  crew: () => api<{ today: string; crew: CrewMember[]; canSignOn?: boolean }>('/office/crew'),
  // Personel kartı (ADR 0131 §1, §2, Ek 1)
  crewProfile: (userId: string) =>
    api<CrewProfileView>(`/office/crew/profiles/${encodeURIComponent(userId)}`),
  createCrewCard: (input: NewCrewCardInput) =>
    api<CrewCardResult>('/office/crew/profiles', { method: 'POST', body: input }),
  updateCrewCard: (userId: string, input: CrewCardUpdate) =>
    api<CrewProfileView>(`/office/crew/profiles/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: input,
    }),
  createCrewLogin: (userId: string, email: string, password: string) =>
    api<CrewCardResult>(`/office/crew/profiles/${encodeURIComponent(userId)}/login`, {
      method: 'POST',
      body: { email, password },
    }),
  removeCrewCard: (userId: string, rowVersion: number, password: string) =>
    api<void>(`/office/crew/profiles/${encodeURIComponent(userId)}/remove`, {
      method: 'POST',
      body: { rowVersion, password },
    }),
  // Joining / Sign-Off (ADR 0131 §3, Ek 2)
  crewMovements: (query: MovementQuery) =>
    api<CrewMovementList>(
      `/office/crew/movements?${new URLSearchParams({ tab: query.tab, from: query.from, to: query.to })}`,
    ),
  signOn: (input: SignOnRequest) =>
    api<MovementResult>('/office/crew/sign-on', { method: 'POST', body: input }),
  crewMovement: (assignmentId: string, action: string, body: object) =>
    api<MovementResult>(`/office/crew/assignments/${encodeURIComponent(assignmentId)}/${action}`, {
      method: 'POST',
      body,
    }),
  movementEvents: (assignmentId: string) =>
    api<{ events: MovementEvent[] }>(
      `/office/crew/assignments/${encodeURIComponent(assignmentId)}/events`,
    ).then((result) => result.events),
  certificateTypes: () => api<CertificateType[]>('/office/certificate-types'),
  crewCertificates: (userId: string) =>
    api<CrewCertificates>(`/office/crew/${userId}/certificates`),
  addCertificate: (userId: string, input: CertificateInput) =>
    api<{ id: string }>(`/office/crew/${userId}/certificates`, { method: 'POST', body: input }),
  updateCertificate: (id: string, rowVersion: number, input: CertificateInput) =>
    api<{ id: string }>(`/office/certificates/${id}`, {
      method: 'PATCH',
      body: { rowVersion, ...input },
    }),
  restHours: (vesselId: string, from: string, to: string) =>
    api<{ vessel: { id: string; name: string; timeZone: string }; crew: CrewRestReport[] }>(
      `/office/vessels/${vesselId}/rest-hours?from=${from}&to=${to}`,
    ),
  workPeriods: (vesselId: string, userId: string, from: string, to: string) =>
    api<WorkPeriod[]>(
      `/office/vessels/${vesselId}/work-periods?userId=${userId}&from=${from}&to=${to}`,
    ),
  restMonth: (vesselId: string, userId: string, month: string) =>
    api<RestMonth>(`/office/vessels/${vesselId}/rest-months?userId=${userId}&month=${month}`),
  // Sefer (ADR 0022): ofis planlar; raporlar gemiden gelir, ofiste salt okunur
  voyages: (vesselId: string) => api<Voyage[]>(`/office/vessels/${vesselId}/voyages`),
  voyageReports: (voyageId: string) => api<VoyageReport[]>(`/office/voyages/${voyageId}/reports`),
  // Sefersiz kalkışlar (ADR 0058 §3)
  unlinkedDepartures: (vesselId: string) =>
    api<VoyageReport[]>(`/office/vessels/${vesselId}/unlinked-departures`),
  /** All Reports (ADR 0082): geminin bütün sefer raporları. */
  vesselVoyageReports: (vesselId: string) =>
    api<VoyageReport[]>(`/office/vessels/${vesselId}/voyage-reports`),
  voyagePerformance: (voyageId: string) =>
    api<VoyagePerformanceView>(`/office/voyages/${voyageId}/performance`),
  // Sefersiz raporlar, bağ önerisi ve sefer talebi (ADR 0083): ofis talep gönderir, gemi uygular
  unassignedReports: (vesselId: string) =>
    api<VoyageReport[]>(`/office/vessels/${vesselId}/unassigned-reports`),
  linkSuggestions: (voyageId: string, start?: string | null) =>
    api<VoyageReport[]>(
      `/office/voyages/${voyageId}/link-suggestions${start ? `?start=${encodeURIComponent(start)}` : ''}`,
    ),
  voyageRequests: (vesselId: string) =>
    api<VoyageOfficeRequest[]>(`/office/vessels/${vesselId}/voyage-requests`),
  submitVoyageRequest: (vesselId: string, body: VoyageRequestInput) =>
    api<VoyageOfficeRequest>(`/office/vessels/${vesselId}/voyage-requests`, {
      method: 'POST',
      body,
    }),
  // Charter party register (ADR 0084): ofis yazar, gemide salt okunur
  charterParties: (vesselId: string) =>
    api<CharterPartyRegisterData>(`/office/vessels/${vesselId}/charter-parties`),
  createCharterParty: (vesselId: string, content: unknown) =>
    api<CharterPartyItem>(`/office/vessels/${vesselId}/charter-parties`, {
      method: 'POST',
      body: content,
    }),
  updateCharterParty: (id: string, rowVersion: number, content: unknown) =>
    api<CharterPartyItem>(`/office/charter-parties/${id}`, {
      method: 'PATCH',
      body: { rowVersion, content },
    }),
  removeCharterParty: (id: string, rowVersion: number) =>
    api<void>(`/office/charter-parties/${id}?rowVersion=${rowVersion}`, { method: 'DELETE' }),
  charterPartyAnalysis: (id: string) =>
    api<CharterPartyAnalysisData>(`/office/charter-parties/${id}/analysis`),
  reviewCharterParty: (id: string, analysisHash: string, note: string | null) =>
    api<CharterPartyEventView>(`/office/charter-parties/${id}/review`, {
      method: 'POST',
      body: { analysisHash, note },
    }),
  decideCharterParty: (vesselId: string, body: CharterPartyDecisionInput) =>
    api<CharterPartyEventView>(`/office/vessels/${vesselId}/charter-party-events`, {
      method: 'POST',
      body,
    }),
  // Limanlar ve mesafe tablosu (ADR 0048): ofis yazar; güzergâh ofiste salt okunur
  voyageRoute: (voyageId: string) => api<VoyageRoute>(`/office/voyages/${voyageId}/route`),
  companyPorts: () => api<CompanyPort[]>('/office/ports/company'),
  // Liman aramaları, salt okunur (M13f; ADR 0052 §3)
  portCalls: () => api<PortCallList>('/office/port-calls'),
  // Mürettebat bordrosu, salt okunur (M16; ADR 0061 §5)
  payroll: (query: { vesselId?: string | undefined; month?: string | undefined }) =>
    api<PayrollData>(`/office/payroll${queryString(query)}`),
  // Gemi saati kaydı, salt okunur (M18; ADR 0065 §9)
  shipClock: (query: { vesselId?: string | undefined }) =>
    api<ShipClockData>(`/office/ship-clock${queryString(query)}`),
  portCall: (id: string) => api<PortCall>(`/office/port-calls/${encodeURIComponent(id)}`),
  createCompanyPort: (input: CompanyPortInput) =>
    api<CompanyPort>('/office/ports/company', { method: 'POST', body: input }),
  updateCompanyPort: (id: string, rowVersion: number, input: Omit<CompanyPortInput, 'code'>) =>
    api<CompanyPort>(`/office/ports/company/${id}`, {
      method: 'PATCH',
      body: { rowVersion, ...input },
    }),
  removeCompanyPort: (id: string, rowVersion: number) =>
    api<{ id: string }>(`/office/ports/company/${id}?rowVersion=${rowVersion}`, {
      method: 'DELETE',
    }),
  portDistances: () => api<PortDistance[]>('/office/ports/distances'),
  createPortDistance: (input: PortDistanceInput) =>
    api<PortDistance>('/office/ports/distances', { method: 'POST', body: input }),
  updatePortDistance: (id: string, patch: PortDistancePatch) =>
    api<PortDistance>(`/office/ports/distances/${id}`, { method: 'PATCH', body: patch }),
  removePortDistance: (id: string, rowVersion: number) =>
    api<{ id: string }>(`/office/ports/distances/${id}?rowVersion=${rowVersion}`, {
      method: 'DELETE',
    }),
  portDistanceHistory: (id: string) =>
    api<PortDistanceHistoryEvent[]>(`/office/ports/distances/${id}/history`),
  importPortDistances: (text: string, dryRun: boolean) =>
    api<DistanceImportReport>('/office/ports/distances/import', {
      method: 'POST',
      body: { text, dryRun },
    }),
  // Malzeme kataloğu ve gemi stoğu (ADR 0026): katalog ofiste; stok gemide, ofiste salt okunur
  materialCategories: () => api<MaterialCategory[]>('/office/material-categories'),
  materials: (query: MaterialQuery) =>
    api<MaterialList>(`/office/materials${queryString({ ...query })}`),
  material: (id: string) => api<MaterialDetail>(`/office/materials/${id}`),
  createMaterial: (input: MaterialInput) =>
    api<MaterialDetail>('/office/materials', { method: 'POST', body: input }),
  updateMaterial: (id: string, patch: Record<string, unknown>) =>
    api<MaterialDetail>(`/office/materials/${id}`, { method: 'PATCH', body: patch }),
  // Sınıf verilirse yalnız o havuz (ADR 0122 §3)
  vesselStock: (vesselId: string, options: { materialClass?: MaterialClass } = {}) =>
    api<VesselStock>(
      `/office/vessels/${vesselId}/stock${queryString({ class: options.materialClass })}`,
    ),
  stockMovements: (
    vesselId: string,
    options: { stockItemId?: string; limit?: number; materialClass?: MaterialClass } = {},
  ) =>
    api<StockMovement[]>(
      `/office/vessels/${vesselId}/stock-movements${queryString({ item: options.stockItemId, limit: options.limit, class: options.materialClass })}`,
    ),
  // Malzeme talebi (ADR 0026 §3): gemide yazılır; ofiste liste, kart ve ofisteki adımın kararı
  requisitions: () => api<RequisitionList>('/office/requisitions'),
  requisition: (id: string) => api<RequisitionDetail>(`/office/requisitions/${id}`),
  // Zil (ADR 0091): kararımı bekleyen talepler
  requisitionsPending: () => api<{ items: PendingRequisition[] }>('/office/requisitions/pending'),
  /** Ofis onayı bekleyen gemide düzenlenen sertifikalar (ADR 0116 §2). */
  certificateIssuancesPending: () =>
    api<{ items: PendingIssuanceNotice[] }>('/office/vessel-certificates/pending-issuances'),
  decideRequisition: (id: string, input: RequisitionDecision) =>
    api<RequisitionDetail>(`/office/requisitions/${id}/decision`, { method: 'POST', body: input }),
  // Ofisin talep revizyonu (ADR 0111 §A): onaylı liste yalnız o gemiye iner
  reviseRequisition: (id: string, input: RequisitionRevisionInput) =>
    api<RequisitionDetail>(`/office/requisitions/${id}/revisions`, { method: 'POST', body: input }),
  // Tedarikçiler (ADR 0026 §1): yalnız ofiste
  suppliers: () => api<SupplierList>('/office/suppliers'),
  createSupplier: (input: SupplierInput) =>
    api<Supplier>('/office/suppliers', { method: 'POST', body: input }),
  updateSupplier: (id: string, rowVersion: number, input: SupplierInput) =>
    api<Supplier>(`/office/suppliers/${id}`, { method: 'PATCH', body: { ...input, rowVersion } }),
  /** Tedarikçi portalına davet ve iptal (ADR 0112 §5). */
  inviteSupplierToPortal: (id: string, body: { email: string; language: 'en' | 'tr' }) =>
    api<{ status: string; email: string; resent: boolean }>(
      `/office/suppliers/${id}/portal/invite`,
      { method: 'POST', body },
    ),
  revokeSupplierPortal: (id: string, reason: string) =>
    api<{ status: string }>(`/office/suppliers/${id}/portal/revoke`, {
      method: 'POST',
      body: { reason },
    }),
  /** Partner dizini: portala kayıtlı tedarikçiler (ADR 0113 §5). */
  partners: (query: PartnerQuery) => {
    const params = new URLSearchParams(
      Object.entries(query).filter((entry): entry is [string, string] => Boolean(entry[1])),
    );
    const text = params.toString();
    return api<{ items: Partner[] }>(`/office/partners${text ? `?${text}` : ''}`);
  },
  /** Açık pazardaki aday tedarikçiyi şirket tedarikçisi yapar (ADR 0113 §4). */
  acceptPortalSupplier: (id: string) =>
    api<Supplier>(`/office/suppliers/${id}/portal/accept`, { method: 'POST', body: {} }),
  // Tedarikçi kartında şirket profili ve banka hesapları (ADR 0114)
  supplierProfile: (id: string) => api<SupplierProfileView>(`/office/suppliers/${id}/profile`),
  revealSupplierBank: (id: string, versionId: string) =>
    api<RevealedBankNumber>(`/office/suppliers/${id}/bank/${versionId}/reveal`, {
      method: 'POST',
      body: {},
    }),
  reviewSupplierBank: (
    id: string,
    versionId: string,
    input: { action: BankReviewAction; note?: string; detailsHash: string; password?: string },
  ) =>
    api<SupplierProfileView>(`/office/suppliers/${id}/bank/${versionId}/review`, {
      method: 'POST',
      body: input,
    }),
  supplierBankPending: () =>
    api<{ items: PendingBankChange[] }>('/office/supplier-bank-reviews/pending'),
  // Zil: incelenecek tedarikçi faturaları ve onaylanacak ödemeler (ADR 0119 Ek 2026-10-24)
  supplierLedgerPending: () => api<PendingLedger>('/office/supplier-ledger/pending'),
  // Partner yorumları (ADR 0120 §4): yayındakiler yazan şirketsiz
  supplierReviews: (id: string) => api<SupplierReviewsView>(`/office/suppliers/${id}/reviews`),
  writeSupplierReview: (
    id: string,
    input: { purchaseOrderId: string; rating: number; comment: string },
  ) => api<SupplierReviewsView>(`/office/suppliers/${id}/reviews`, { method: 'POST', body: input }),
  // RFQ ve teklif karşılaştırması (ADR 0026 §1; M12b Task 14): yalnız ofiste
  rfqs: () => api<RfqList>('/office/rfqs'),
  rfq: (id: string) => api<RfqDetail>(`/office/rfqs/${id}`),
  createRfq: (input: RfqInput) => api<RfqDetail>('/office/rfqs', { method: 'POST', body: input }),
  inviteRfqSupplier: (id: string, supplierId: string) =>
    api<RfqDetail>(`/office/rfqs/${id}/suppliers`, { method: 'POST', body: { supplierId } }),
  sendRfq: (id: string, rowVersion: number) =>
    api<RfqDetail>(`/office/rfqs/${id}/send`, { method: 'POST', body: { rowVersion } }),
  saveQuotation: (id: string, supplierId: string, input: QuotationInput) =>
    api<RfqDetail>(`/office/rfqs/${id}/quotations/${supplierId}`, { method: 'PUT', body: input }),
  selectQuotation: (
    id: string,
    input: { rowVersion: number; quotationId: string; reason: string | null },
  ) => api<RfqDetail>(`/office/rfqs/${id}/select`, { method: 'POST', body: input }),
  cancelRfq: (id: string, input: { rowVersion: number; reason: string }) =>
    api<RfqDetail>(`/office/rfqs/${id}/cancel`, { method: 'POST', body: input }),
  /** Tedarikçi portalına yayın (ADR 0111 §C). */
  publishRfq: (id: string, input: RfqPublishInput) =>
    api<RfqDetail>(`/office/rfqs/${id}/publish`, { method: 'POST', body: input }),
  /** RFQ ▸ Award: bölme, tur, kesinleştirme ve yorum (ADR 0111 §D). */
  rfqAwards: (rfqId: string) => api<RfqAwards>(`/office/rfqs/${rfqId}/awards`),
  createAward: (rfqId: string, input: { supplierId: string; proposal: AwardProposalInput }) =>
    api<RfqAwards>(`/office/rfqs/${rfqId}/awards`, { method: 'POST', body: input }),
  proposeAward: (awardId: string, input: { roundNo: number; proposal: AwardProposalInput }) =>
    api<RfqAwards>(`/office/awards/${awardId}/proposals`, { method: 'POST', body: input }),
  acceptAwardCounter: (awardId: string, input: { roundNo: number }) =>
    api<RfqAwards>(`/office/awards/${awardId}/accept-counter`, { method: 'POST', body: input }),
  finalizeAward: (awardId: string, input: { roundNo: number; password?: string }) =>
    api<RfqAwards>(`/office/awards/${awardId}/finalize`, { method: 'POST', body: input }),
  cancelAward: (awardId: string, input: { reason: string }) =>
    api<RfqAwards>(`/office/awards/${awardId}/cancel`, { method: 'POST', body: input }),
  commentAward: (awardId: string, input: { body: string }) =>
    api<RfqAwards>(`/office/awards/${awardId}/comments`, { method: 'POST', body: input }),
  purchaseOrders: () => api<PurchaseOrderList>('/office/purchase-orders'),
  purchaseOrderCandidates: () =>
    api<PurchaseOrderCandidate[]>('/office/purchase-orders/candidates'),
  purchaseOrder: (id: string) => api<PurchaseOrderDetail>(`/office/purchase-orders/${id}`),
  createPurchaseOrder: (rfqId: string) =>
    api<PurchaseOrderDetail>('/office/purchase-orders', { method: 'POST', body: { rfqId } }),
  updatePurchaseOrder: (id: string, rowVersion: number, input: PurchaseOrderDeliveryInput) =>
    api<PurchaseOrderDetail>(`/office/purchase-orders/${id}`, {
      method: 'PATCH',
      body: { ...input, rowVersion },
    }),
  purchaseOrderAction: (id: string, action: string, body: Record<string, unknown>) =>
    api<PurchaseOrderDetail>(`/office/purchase-orders/${id}/${action}`, { method: 'POST', body }),
  goodsReceipts: () => api<GoodsReceiptList>('/office/goods-receipts'),
  goodsReceipt: (id: string) => api<GoodsReceiptDetail>(`/office/goods-receipts/${id}`),
  report: (report: ReportKind, query: ReportQuery) =>
    api<ReportData>(
      `/office/reports/${report}${queryString({
        vessel: query.vessel ?? undefined,
        from: query.from ?? undefined,
        to: query.to ?? undefined,
        status: query.status ?? undefined,
      })}`,
    ),
  dashboard: () => api<OfficeDashboardSummary>('/office/dashboard'),
  branding: () => api<{ branding: Branding | null }>('/office/branding'),
  saveBranding: (input: BrandingInput) =>
    api<{ branding: Branding | null }>('/office/branding', { method: 'PUT', body: input }),
  removeCertificate: (id: string, rowVersion: number) =>
    api<{ id: string }>(`/office/certificates/${id}?rowVersion=${rowVersion}`, {
      method: 'DELETE',
    }),
  // PMS ekipmanı (M7a; ADR 0029): kurulum devrinde ofis hazırlar, etkinleşmiş gemide salt okunur
  particulars: (vesselId: string) =>
    api<VesselParticulars>(`/office/vessels/${vesselId}/particulars`),
  saveParticulars: (vesselId: string, rowVersion: number, input: Partial<VesselParticularsInput>) =>
    api<VesselParticulars>(`/office/vessels/${vesselId}/particulars`, {
      method: 'PATCH',
      body: { rowVersion, ...input },
    }),
  equipmentTree: (vesselId: string) =>
    api<EquipmentTree>(`/office/vessels/${vesselId}/pms/equipment/tree`),
  equipment: (id: string) => api<EquipmentDetail>(`/office/pms/equipment/${id}`),
  equipmentReadings: (id: string) => api<Reading[]>(`/office/pms/equipment/${id}/readings`),
  createEquipment: (vesselId: string, input: Record<string, unknown>) =>
    api<EquipmentDetail | QueuedSetupChange>(`/office/vessels/${vesselId}/pms/equipment`, {
      method: 'POST',
      body: input,
    }),
  updateEquipment: (id: string, patch: Record<string, unknown>) =>
    api<EquipmentDetail | QueuedSetupChange>(`/office/pms/equipment/${id}`, {
      method: 'PATCH',
      body: patch,
    }),
  decommissionEquipment: (id: string, rowVersion: number, reason: string) =>
    api<EquipmentDetail | QueuedSetupChange>(
      `/office/pms/equipment/${id}${queryString({ rowVersion, reason })}`,
      { method: 'DELETE' },
    ),
  loadDefaultStructure: (vesselId: string) =>
    api<{ created: number }>(`/office/vessels/${vesselId}/pms/equipment/default-structure`, {
      method: 'POST',
      body: {},
    }),
  // PMS işleri (M7b3; ADR 0029): okuma, kurulum devrinde iş tanımı, ofis adımı kararı
  pmsJobs: (vesselId: string) =>
    api<JobList>(`/office/vessels/${vesselId}/pms/jobs${queryString({ includeDeleted: true })}`),
  createPmsJob: (vesselId: string, body: Record<string, unknown>) =>
    api<JobView | QueuedSetupChange>(`/office/vessels/${vesselId}/pms/jobs`, {
      method: 'POST',
      body,
    }),
  updatePmsJob: (id: string, body: Record<string, unknown>) =>
    api<JobView | QueuedSetupChange>(`/office/pms/jobs/${id}`, { method: 'PATCH', body }),
  removePmsJob: (id: string, rowVersion: number, reason: string) =>
    api<JobView | QueuedSetupChange>(
      `/office/pms/jobs/${id}${queryString({ rowVersion, reason })}`,
      {
        method: 'DELETE',
      },
    ),
  pmsPlan: (vesselId: string, range: { from?: string; to?: string } = {}) =>
    api<PlanView>(`/office/vessels/${vesselId}/pms/plan${queryString(range)}`),
  pmsWorkOrders: (vesselId: string) =>
    api<WorkOrderList>(`/office/vessels/${vesselId}/pms/work-orders`),
  pmsWorkOrder: (id: string) => api<WorkOrderView>(`/office/pms/work-orders/${id}`),
  pmsHistory: (vesselId: string, filter: HistoryFilter = {}) =>
    api<{ items: HistoryEntry[] }>(
      `/office/vessels/${vesselId}/pms/history${queryString({ ...filter })}`,
    ),
  pmsPending: () => api<{ items: PendingItem[] }>('/office/pms/pending'),
  /** Denetim kaydı (plan M7 Task 30): bulut tam kaynak. */
  auditTrail: (vesselId: string, filter: AuditTrailFilter = {}) =>
    api<AuditTrailData>(`/office/vessels/${vesselId}/audit${queryString({ ...filter })}`),
  /** NR496 raporunun verisi (plan M7 Task 28–29). */
  pmsReport: (vesselId: string, reportId: string, query: PmsReportFilter = {}) =>
    api<PmsReportView>(
      `/office/vessels/${vesselId}/pms/reports/${reportId}${queryString({ ...query })}`,
    ),
  /** PMS talimatları (K38): Job Card seçicisi yalnız yayındakileri listeler. */
  pmsInstructionTree: () => api<LibraryTree>('/office/documents/tree?section=pms_instructions'),
  pmsAttachments: (
    ownerId: string,
    ownerType:
      | 'work_order'
      | 'work_record'
      | 'port_call'
      | 'voyage_report'
      | 'defect'
      | 'payroll_payment'
      // RA bulgusunun kanıtı ve kasa hareketinin makbuzu (ADR 0121)
      | 'ra_finding'
      | 'cash_transaction'
      // Tatbikat adımının kanıtı (ADR 0128 Faz 3 Ek)
      | 'drill_record_step'
      | 'pdi_result' = 'work_order',
  ) => api<PmsAttachment[]>(`/office/attachments${queryString({ ownerType, ownerId })}`),
  decidePostponement: (id: string, body: DecisionInput) =>
    api<unknown>(`/office/postponements/${id}/decision`, { method: 'POST', body }),
  decideIntervalChange: (id: string, body: DecisionInput) =>
    api<unknown>(`/office/interval-changes/${id}/decision`, { method: 'POST', body }),
  /** Etkin gemide aralığı ofis girer (ADR 0104): karar gemiye gider, gemi uygular. */
  sendIntervalChange: (jobId: string, body: Record<string, unknown>) =>
    api<{ id: string; status: 'sent' }>(`/office/pms/jobs/${jobId}/interval-decisions`, {
      method: 'POST',
      body,
    }),
  // Dolaşan bileşenler (ADR 0133 §6): ofiste salt okunur
  pmsRotables: (vesselId: string) => api<RotableList>(`/office/vessels/${vesselId}/pms/rotables`),
  pmsIntervalHistory: (jobId: string) =>
    api<{ items: IntervalHistoryEntry[]; changes?: JobChangeEntry[] }>(
      `/office/pms/jobs/${jobId}/interval-history`,
    ),
  // Ertelemeler (ADR 0108 §4): gemi ya da filo; ofisin notu ve kararı (§3)
  pmsPostponements: (vesselId: string | null) =>
    api<{ items: PostponementView[] }>(
      `/office/pms/postponements${vesselId ? `?vesselId=${encodeURIComponent(vesselId)}` : ''}`,
    ),
  // Kurulum değişiklikleri ve gemideki sonuçları (ADR 0142 §3): süzgeç ve sayfa sunucuda
  pmsSetupChanges: (vesselId: string, query: SetupChangeQuery = {}) =>
    api<SetupChangeListView>(
      `/office/vessels/${vesselId}/pms/setup-changes${queryString({
        status: query.status ?? undefined,
        table: query.table ?? undefined,
        page: query.page && query.page > 1 ? query.page : undefined,
      })}`,
    ),
  postponementAction: (id: string, body: Record<string, unknown>) =>
    api<{ id: string; status: 'posted' | 'sent' }>(`/office/pms/postponements/${id}/actions`, {
      method: 'POST',
      body,
    }),
  // PMS durum izleme ve arıza (M7c; ADR 0029 K18–K25): okuma; noktalar kurulum devrinde (K1)
  pmsMeasurementPoints: (vesselId: string, filter: { equipmentId?: string } = {}) =>
    api<PointList>(`/office/vessels/${vesselId}/pms/measurement-points${queryString(filter)}`),
  createMeasurementPoint: (vesselId: string, body: Record<string, unknown>) =>
    api<MeasurementPoint>(`/office/vessels/${vesselId}/pms/measurement-points`, {
      method: 'POST',
      body,
    }),
  updateMeasurementPoint: (id: string, body: Record<string, unknown>) =>
    api<MeasurementPoint>(`/office/pms/measurement-points/${id}`, { method: 'PATCH', body }),
  removeMeasurementPoint: (id: string, rowVersion: number, reason: string) =>
    api<unknown>(`/office/pms/measurement-points/${id}${queryString({ rowVersion, reason })}`, {
      method: 'DELETE',
    }),
  pmsMeasurements: (vesselId: string, filter: { pointId?: string; workOrderId?: string }) =>
    api<{ items: Measurement[] }>(
      `/office/vessels/${vesselId}/pms/measurements${queryString(filter)}`,
    ),
  /** CM eşik içe aktarma (K20): dosya önce tus ile yüklenir (amaç `cm_import`). */
  pmsCmImport: (body: { vesselId: string; uploadId: string; fileName: string; preview: boolean }) =>
    api<{ preview?: boolean; report: CmReport; batch?: { id: string } }>('/office/pms/cm-imports', {
      method: 'POST',
      body,
    }),
  pmsDefects: (vesselId: string, filter: { kind?: string; equipmentId?: string } = {}) =>
    api<DefectList>(`/office/vessels/${vesselId}/pms/defects${queryString(filter)}`),
  pmsDefect: (id: string) => api<DefectView>(`/office/pms/defects/${id}`),
  decideDefect: (id: string, body: DecisionInput) =>
    api<unknown>(`/office/pms/defects/${id}/decision`, { method: 'POST', body }),
  // Arıza DM-17 (ADR 0060): filo listesi, ayrıntı, Part C notu ve yönetim kararı
  fleetDefects: () => api<DefectListResult>('/office/pms/defects'),
  defectDetail: (id: string) => api<DefectDetail>(`/office/pms/defects/${id}`),
  defectNote: (
    id: string,
    body: { kind: 'management_action' | 'comment'; body: string; actionOn: string | null },
  ) => api<DefectDetail>(`/office/pms/defects/${id}/notes`, { method: 'POST', body }),
  decideDefectReport: (
    id: string,
    body: {
      contentHash: string;
      submissionId: string;
      step: number;
      decision: 'approved' | 'returned';
      reason?: string;
      /** Yalnız girildiyse (ADR 0075). */
      password?: string;
    },
  ) =>
    api<{ defect: DefectDetail }>(`/office/pms/defects/${id}/decision`, {
      method: 'POST',
      body,
    }),
  pmsAlerts: (vesselId: string) => api<PmsAlertsView>(`/office/vessels/${vesselId}/pms/alerts`),
  /** Settings ▸ PMS (K27, K30, K32): şirketin uyarı ve rapor eşikleri. */
  pmsSettings: () => api<PmsSettings>('/office/settings/pms'),
  /** Ayarlar ▸ Onaylar (ADR 0056 §4). */
  approvalSettings: () => api<ApprovalSettings>('/office/settings/approvals'),
  saveApprovalSettings: (body: { rowVersion: number | null; reauthAlways: boolean }) =>
    api<ApprovalSettings>('/office/settings/approvals', { method: 'PATCH', body }),
  /** Ayarlar ▸ Şirket bilgileri (ADR 0140 §4): salt okunur, yalnız şirket yöneticisi. */
  companyInfo: () =>
    api<{
      profile: CompanyProfile | null;
      contract: { status: TenantContractStatus; endsOn: string | null; daysLeft: number | null };
    }>('/office/settings/company-info'),
  /** Ayarlar ▸ Kaptanın vekili (ADR 0127 §3). */
  masterDeputy: () => api<MasterDeputySettings>('/office/settings/master-deputy'),
  saveMasterDeputy: (body: { rowVersion: number | null; roleId: string | null }) =>
    api<MasterDeputySettings>('/office/settings/master-deputy', { method: 'PATCH', body }),
  savePmsSettings: (body: Partial<PmsSettings> & { rowVersion: number | null }) =>
    api<PmsSettings>('/office/settings/pms', { method: 'PATCH', body }),
  // Settings ▸ Risk Matrix (K1; ADR 0085)
  riskMatrix: () => api<RiskMatrixSettings>('/office/settings/risk-matrix'),
  selectRiskMatrix: (body: { rowVersion: number | null; profileId: string }) =>
    api<RiskMatrixSettings>('/office/settings/risk-matrix', { method: 'PATCH', body }),
  publishRiskMatrix: (
    profileId: string,
    body: { expectedVersion: number; definition: RaMatrixDefinition; reason: string },
  ) =>
    api<RiskMatrixSettings>(`/office/settings/risk-matrix/profiles/${profileId}/versions`, {
      method: 'POST',
      body,
    }),
};
